/**
 * Per-feature landing page data.
 *
 * Each feature gets its own SEO-optimized landing page at /features/:slug.
 * Data drives the FeatureDetailPage template component.
 */

export interface FeaturePageData {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  ogImage: string;
  targetKeywords: string[];
  /** Problem badge text shown above the headline */
  problemBadge: string;
  /** Main headline (H1) */
  headline: string;
  /** Subtitle below headline */
  subtitle: string;
  /** The pain point this feature solves */
  painPoint: {
    title: string;
    description: string;
    stats: string;
  };
  /** How the feature solves the problem */
  solution: {
    title: string;
    description: string;
    steps: Array<{
      title: string;
      description: string;
    }>;
  };
  /** Key metrics this feature provides */
  keyMetrics: Array<{
    label: string;
    description: string;
    icon: string;
  }>;
  /** In-app route for "See It in Action" CTA */
  appRoute: string;
  /** Screenshot path */
  screenshot: string;
  screenshotAlt: string;
  /** Related guide slugs for internal linking */
  relatedGuides: string[];
  /** Related feature slugs for cross-linking between feature pages */
  relatedFeatures?: string[];
  /** Customer quote for social proof */
  testimonial?: {
    quote: string;
    author: string;
    role: string;
  };
  /** Structured data for SEO */
  structuredDataFeatureList: string[];
}

export const featurePages: FeaturePageData[] = [
  {
    slug: 'cycle-time',
    title: 'PR Cycle Time Analytics',
    metaTitle: 'PR Cycle Time Analytics — See Where Engineering Time Goes',
    metaDescription:
      'Break down PR cycle time into coding, waiting, review, and merge phases. Identify bottlenecks and reduce time-to-merge by up to 47%.',
    ogImage: '/images/og-features/cycle-time.webp',
    targetKeywords: [
      'PR cycle time analytics',
      'cycle time breakdown',
      'pull request cycle time',
      'reduce PR cycle time',
      'code review waiting time',
      'engineering cycle time tool',
      'PR bottleneck detection',
      'time to merge analytics',
    ],
    problemBadge: 'Delivery Visibility',
    headline: 'See Exactly Where Engineering Time Goes',
    subtitle:
      'Break down every pull request into coding, waiting, review, and merge phases. Find the bottleneck that is actually slowing your team down.',
    painPoint: {
      title: "You can't improve what you can't see",
      description:
        'Leaders know delivery is slow but can not explain why. Is it coding taking too long? Reviews sitting idle? Unclear handoffs? Without phase-level visibility, every standup is guesswork.',
      stats: '92% of cycle time is spent waiting for review, not coding — 2025 CodePulse Benchmark Study (803K PRs)',
    },
    solution: {
      title: 'The 4-Stage Pipeline Model',
      description:
        'CodePulse breaks every PR lifecycle into four distinct phases, measured automatically from your GitHub data.',
      steps: [
        {
          title: 'Coding Phase',
          description:
            'Time from first commit to PR open. See if developers are batching work or shipping incrementally.',
        },
        {
          title: 'Waiting for Review',
          description:
            'Time from PR open to first review. The biggest bottleneck for most teams — and the easiest to fix.',
        },
        {
          title: 'In Review',
          description:
            'Time from first review to approval. Track review thoroughness vs. rubber-stamping.',
        },
        {
          title: 'Merge Phase',
          description:
            'Time from approval to merge. Detect CI/CD delays, merge conflicts, and forgotten PRs.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Median Cycle Time', description: 'End-to-end time from first commit to merge', icon: 'clock' },
      { label: 'Wait for Review', description: 'Average time PRs sit before first review', icon: 'pause' },
      { label: 'Review Duration', description: 'Time spent in active review', icon: 'users' },
      { label: 'Merge Delay', description: 'Time between approval and merge', icon: 'git-merge' },
    ],
    appRoute: '/dashboard',
    screenshot: '/images/screenshots/dashboard-velocity-graph.png',
    screenshotAlt: 'CodePulse dashboard showing PR cycle time breakdown by phase',
    relatedGuides: [
      'cycle-time-breakdown-guide',
      'reduce-pr-cycle-time',
      'pr-cycle-time-benchmarks',
      'deployment-frequency-lead-time-guide',
    ],
    relatedFeatures: ['dora-metrics', 'review-network', 'forecasting'],
    testimonial: {
      quote: 'We found that 92% of our cycle time was waiting for review. Fixed in 3 weeks with simple reviewer rotation.',
      author: 'Jonathon Turner',
      role: 'Engineering Manager @ Ultra Communications',
    },
    structuredDataFeatureList: [
      'PR Cycle Time Breakdown',
      'Waiting for Review Detection',
      'Phase-by-Phase Analytics',
      'Trend Tracking Over Time',
      'DORA Lead Time Correlation',
    ],
  },
  {
    slug: 'dora-metrics',
    title: 'DORA Metrics Dashboard',
    metaTitle: 'DORA Metrics Dashboard — Measure What Elite Teams Measure',
    metaDescription:
      'Track all four DORA metrics from GitHub data. Benchmark against industry standards and see your team\'s performance tier.',
    ogImage: '/images/og-features/dora-metrics.webp',
    targetKeywords: [
      'DORA metrics dashboard',
      'DORA metrics tool',
      'four key metrics',
      'deployment frequency tracking',
      'lead time for changes',
      'change failure rate',
      'DORA benchmarks',
      'engineering performance metrics',
    ],
    problemBadge: 'Performance Measurement',
    headline: 'Track the Metrics Elite Teams Actually Use',
    subtitle:
      'DORA metrics are the industry standard for measuring software delivery. See where your team stands — and what to improve first.',
    painPoint: {
      title: 'Measuring the wrong things leads to wrong decisions',
      description:
        'Lines of code, commits per day, story points — none of these predict delivery performance. DORA metrics do, but most teams have no way to measure them.',
      stats: 'Elite teams deploy 973x more frequently than low performers — 2024 DORA Report',
    },
    solution: {
      title: 'Four Metrics, One Dashboard',
      description:
        'CodePulse derives all four DORA metrics from your GitHub pull request and merge data — no CI/CD pipeline integration required.',
      steps: [
        {
          title: 'Deployment Frequency',
          description: 'How often your team ships to production, measured by PR merge rate.',
        },
        {
          title: 'Lead Time for Changes',
          description: 'Time from first commit to production deployment. Separate from PR cycle time.',
        },
        {
          title: 'Change Failure Rate',
          description: 'Percentage of merges that cause issues, detected via revert PRs and hotfix patterns.',
        },
        {
          title: 'Mean Time to Recovery',
          description: 'How quickly your team responds to failures, measured by hotfix cycle time.',
        },
      ],
    },
    keyMetrics: [
      { label: 'DORA Tier', description: 'Elite, High, Medium, or Low classification', icon: 'award' },
      { label: 'Deploy Frequency', description: 'Merges per day/week with trend', icon: 'rocket' },
      { label: 'Lead Time', description: 'Median first-commit-to-production duration', icon: 'clock' },
      { label: 'Failure Rate', description: 'Revert and hotfix detection', icon: 'alert-triangle' },
    ],
    appRoute: '/dashboard',
    screenshot: '/images/screenshots/executive-summary.png',
    screenshotAlt: 'CodePulse DORA metrics dashboard with performance tier and benchmarks',
    relatedGuides: [
      'dora-four-keys-implementation-guide',
      'dora-metrics-guide',
      'deployment-frequency-lead-time-guide',
      'best-dora-metrics-tools',
    ],
    relatedFeatures: ['cycle-time', 'deployment-analytics', 'executive-summary'],
    testimonial: {
      quote: 'Finally, a DORA dashboard that works out of the box without wiring up CI/CD pipelines.',
      author: 'Robert Striker',
      role: 'VP Engineering @ Article Fiesta',
    },
    structuredDataFeatureList: [
      'All Four DORA Metrics',
      'Industry Benchmarking',
      'Performance Tier Classification',
      'Trend Analysis',
      'No CI/CD Integration Required',
    ],
  },
  {
    slug: 'review-network',
    title: 'Code Review Network Analysis',
    metaTitle: 'Code Review Network Analysis — Visualize Who Reviews Whose Code',
    metaDescription:
      'Interactive network graph showing review relationships across your team. Spot bottlenecks, imbalances, and isolated contributors.',
    ogImage: '/images/og-features/review-network.webp',
    targetKeywords: [
      'code review analytics',
      'review network visualization',
      'code review bottleneck',
      'reviewer workload balance',
      'pull request review analysis',
      'code review patterns',
      'review load distribution',
      'peer review analytics',
    ],
    problemBadge: 'Review Bottlenecks',
    headline: 'See Who Reviews Whose Code — And Who Doesn\'t',
    subtitle:
      'Interactive network visualization of review relationships. Find the 2-3 people who review everything, and the ones who never get asked.',
    painPoint: {
      title: 'The same 2 people review everything',
      description:
        'Most teams have an invisible review bottleneck: a handful of senior developers who review 80% of PRs while others barely participate. This creates delays, silos, and burnout.',
      stats: '71% of PRs in our 2025 study were self-merged with zero review — CodePulse 2025 Benchmark',
    },
    solution: {
      title: 'Review Collaboration Map',
      description:
        'An interactive force-directed graph showing every review relationship on your team, weighted by frequency.',
      steps: [
        {
          title: 'Relationship Mapping',
          description: 'See directional review relationships — who requests reviews from whom, and how often.',
        },
        {
          title: 'Bottleneck Detection',
          description: 'Spot over-concentrated reviewers with outsized node sizes in the graph.',
        },
        {
          title: 'Isolation Detection',
          description: 'Find team members with no review connections — potential knowledge silo risks.',
        },
        {
          title: 'Load Balancing',
          description: 'Review-to-author ratio metrics help distribute load fairly across the team.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Review Load Ratio', description: 'Reviews given vs. PRs authored per developer', icon: 'scale' },
      { label: 'Avg Reviews/PR', description: 'Number of reviewers per pull request', icon: 'users' },
      { label: 'Review Coverage', description: 'Percentage of PRs with at least one review', icon: 'shield' },
      { label: 'Self-Merge Rate', description: 'PRs merged without any peer review', icon: 'alert-circle' },
    ],
    appRoute: '/review-network',
    screenshot: '/images/screenshots/review-network.png',
    screenshotAlt: 'CodePulse review network graph showing reviewer relationships and bottlenecks',
    relatedGuides: [
      'review-load-balancing-guide',
      'code-review-culture-sentiment',
      'async-code-review-distributed-teams',
      'reduce-pr-cycle-time',
    ],
    relatedFeatures: ['knowledge-silos', 'developer-comparison', 'review-insights'],
    structuredDataFeatureList: [
      'Interactive Review Network Graph',
      'Reviewer Workload Distribution',
      'Self-Merge Detection',
      'Review Coverage Metrics',
      'Bottleneck Identification',
    ],
  },
  {
    slug: 'knowledge-silos',
    title: 'Knowledge Silo Detection',
    metaTitle: 'Knowledge Silo Detection — Find Bus Factor 1 Code Before It Hurts',
    metaDescription:
      'Automatically detect code owned by a single contributor. Visualize knowledge distribution and prioritize cross-training.',
    ogImage: '/images/og-features/knowledge-silos.webp',
    targetKeywords: [
      'knowledge silo detection',
      'bus factor analysis',
      'code ownership visualization',
      'single contributor risk',
      'knowledge distribution',
      'code ownership metrics',
      'bus factor tool',
      'knowledge concentration risk',
    ],
    problemBadge: 'Knowledge Risk',
    headline: 'Find the Code Only One Person Understands',
    subtitle:
      'Automatically detect files and directories where knowledge is concentrated in a single contributor. Fix bus factor risks before they become emergencies.',
    painPoint: {
      title: 'Bus factor of 1 is a business risk',
      description:
        'Critical code owned by one developer is an operational risk. When they go on vacation, get sick, or leave — the team is stuck. Most leaders do not know which code has this problem.',
      stats: 'In our study of 802,979 merged pull requests, 71% shipped with no second reviewer - that is how code ends up understood by exactly one person',
    },
    solution: {
      title: 'Bus Factor Risk Matrix',
      description:
        'CodePulse analyzes commit history to calculate knowledge distribution for every file and directory.',
      steps: [
        {
          title: 'Ownership Analysis',
          description: 'See which developers have contributed to each file, weighted by recency and volume.',
        },
        {
          title: 'Risk Scoring',
          description: 'Files are scored by knowledge concentration — bus factor 1 files are flagged immediately.',
        },
        {
          title: 'Hotspot Detection',
          description: 'Cross-reference with change frequency to find high-churn, single-owner files.',
        },
        {
          title: 'Cross-Training Priority',
          description: 'Ranked list of files and directories that most need knowledge sharing.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Bus Factor Score', description: 'Number of contributors per file/directory', icon: 'users' },
      { label: 'Risk Files', description: 'Files with bus factor of 1', icon: 'alert-triangle' },
      { label: 'Change Frequency', description: 'How often high-risk files are modified', icon: 'activity' },
      { label: 'Knowledge Spread', description: 'Distribution of ownership across team', icon: 'pie-chart' },
    ],
    appRoute: '/file-hotspots',
    screenshot: '/images/screenshots/file-hotspots.png',
    screenshotAlt: 'CodePulse file hotspots showing knowledge concentration and bus factor risks',
    relatedGuides: [
      'code-hotspots-knowledge-silos',
      'monorepo-multi-repo-metrics',
      'review-load-balancing-guide',
      'engineering-health-scorecard',
    ],
    relatedFeatures: ['file-hotspots', 'review-network', 'risky-changes'],
    structuredDataFeatureList: [
      'Bus Factor Analysis',
      'Knowledge Distribution Heatmap',
      'High-Churn File Detection',
      'Cross-Training Prioritization',
      'Directory-Level Ownership',
    ],
  },
  {
    slug: 'developer-comparison',
    title: 'Developer Comparison & Insights',
    metaTitle: 'Developer Comparison & Insights — Team-Level Performance Visibility',
    metaDescription:
      'Compare developer contributions across velocity, quality, and collaboration metrics. Built for mentoring, not surveillance.',
    ogImage: '/images/og-features/developer-comparison.webp',
    targetKeywords: [
      'developer comparison tool',
      'developer performance metrics',
      'engineering team comparison',
      'developer productivity insights',
      'developer analytics dashboard',
      'team performance comparison',
      'developer contribution metrics',
      'engineering team insights',
    ],
    problemBadge: 'Team Visibility',
    headline: 'Understand Contribution Patterns Without Surveillance',
    subtitle:
      'Compare developers across velocity, quality, and collaboration metrics. Designed for 1:1 mentoring conversations, not stack ranking.',
    painPoint: {
      title: 'Productivity discussions without data are opinion wars',
      description:
        'Engineering managers need objective data for performance conversations, headcount justification, and capacity planning. Without it, every discussion is based on feelings.',
      stats: '83% of engineering leaders say they lack objective data for performance conversations — Gartner 2024',
    },
    solution: {
      title: 'Anti-Surveillance Analytics',
      description:
        'CodePulse shows contribution patterns without turning metrics into individual scorecards. Every view is designed for coaching, not punishment.',
      steps: [
        {
          title: 'Multi-Dimensional View',
          description: 'Compare across velocity, quality, review participation, and collaboration — not just output.',
        },
        {
          title: 'Trend Over Snapshot',
          description: 'See how patterns change over time, not just a single-point comparison.',
        },
        {
          title: 'Context-Rich Data',
          description: 'PR complexity, review load, and team context prevent misleading comparisons.',
        },
        {
          title: 'Mentoring Focus',
          description: 'Designed for 1:1 prep: "I noticed your review load tripled this sprint — let\'s talk about it."',
        },
      ],
    },
    keyMetrics: [
      { label: 'Velocity Score', description: 'Composite measure of delivery speed', icon: 'zap' },
      { label: 'Review Participation', description: 'Reviews given and received', icon: 'message-circle' },
      { label: 'PR Quality', description: 'Size, review coverage, and approval patterns', icon: 'check-circle' },
      { label: 'Collaboration Index', description: 'Cross-team review and co-authoring', icon: 'users' },
    ],
    appRoute: '/developers',
    screenshot: '/images/screenshots/developer-comparison.png',
    screenshotAlt: 'CodePulse developer comparison showing multi-dimensional contribution analysis',
    relatedGuides: [
      'developer-recognition-awards',
      'review-load-balancing-guide',
      'engineering-health-scorecard',
      'board-ready-engineering-metrics',
    ],
    relatedFeatures: ['review-network', 'executive-summary', 'space-framework'],
    structuredDataFeatureList: [
      'Multi-Dimensional Developer Comparison',
      'Trend Analysis Over Time',
      'Review Participation Metrics',
      'Anti-Surveillance Design',
      'Mentoring-Focused Insights',
    ],
  },
  {
    slug: 'file-hotspots',
    title: 'File Hotspot Analysis',
    metaTitle: 'File Hotspot Analysis — Find High-Churn Code That Needs Attention',
    metaDescription:
      'Identify the most frequently changed files in your codebase. Correlate churn with complexity and ownership to prioritize refactoring.',
    ogImage: '/images/og-features/file-hotspots.webp',
    targetKeywords: [
      'code hotspot analysis',
      'file churn detection',
      'high-change file analysis',
      'code complexity metrics',
      'refactoring prioritization',
      'code churn analytics',
      'hotspot detection tool',
      'technical debt detection',
    ],
    problemBadge: 'Code Health',
    headline: 'Find the Files That Keep Breaking Things',
    subtitle:
      'High-churn files with concentrated ownership are where bugs hide. Identify them before they cause production incidents.',
    painPoint: {
      title: 'Not all code changes carry the same risk',
      description:
        'A file changed 50 times in the last month by one developer is a higher risk than a file changed once by three contributors. Most teams have no way to distinguish between the two.',
      stats: '20% of files account for 80% of defects in most codebases — IEEE Software Engineering research',
    },
    solution: {
      title: 'Churn + Ownership Matrix',
      description:
        'CodePulse combines change frequency with ownership data to surface the riskiest files in your codebase.',
      steps: [
        {
          title: 'Change Frequency Analysis',
          description: 'Track how often each file is modified across PRs, weighted by recency.',
        },
        {
          title: 'Ownership Overlay',
          description: 'See how many contributors touch each hotspot — single-owner hotspots are highest risk.',
        },
        {
          title: 'Complexity Correlation',
          description: 'Large files with high churn and low ownership are your top refactoring candidates.',
        },
        {
          title: 'Directory Aggregation',
          description: 'Roll up to directory level to identify entire modules that need architectural attention.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Churn Score', description: 'Weighted change frequency per file', icon: 'refresh-cw' },
      { label: 'Ownership Depth', description: 'Number of unique contributors', icon: 'users' },
      { label: 'Risk Score', description: 'Combined churn + ownership risk', icon: 'alert-triangle' },
      { label: 'Lines Changed', description: 'Total additions and deletions', icon: 'code' },
    ],
    appRoute: '/file-hotspots',
    screenshot: '/images/screenshots/file-hotspots.png',
    screenshotAlt: 'CodePulse file hotspot analysis showing churn patterns and ownership distribution',
    relatedGuides: [
      'code-hotspots-knowledge-silos',
      'pr-size-optimization-guide',
      'test-failure-rate-guide',
      'data-quality-engineering-metrics',
    ],
    relatedFeatures: ['knowledge-silos', 'risky-changes', 'cycle-time'],
    structuredDataFeatureList: [
      'File Change Frequency Tracking',
      'Ownership Distribution Analysis',
      'Risk Score Calculation',
      'Directory-Level Aggregation',
      'Refactoring Prioritization',
    ],
  },
  {
    slug: 'executive-summary',
    title: 'Executive Engineering Summary',
    metaTitle: 'Executive Engineering Summary — Board-Ready Metrics in 60 Seconds',
    metaDescription:
      'One-page engineering performance summary for leadership conversations. DORA benchmarks, health grades, and trend analysis.',
    ogImage: '/images/og-features/executive-summary.webp',
    targetKeywords: [
      'executive engineering dashboard',
      'engineering metrics for leadership',
      'board-ready engineering metrics',
      'engineering performance summary',
      'CTO dashboard',
      'VP engineering metrics',
      'engineering health report',
      'engineering executive summary',
    ],
    problemBadge: 'Leadership Reporting',
    headline: 'Board-Ready Engineering Metrics in 60 Seconds',
    subtitle:
      'A single-page view of engineering performance designed for non-technical stakeholders. Health grades, DORA benchmarks, and trend data.',
    painPoint: {
      title: 'Explaining engineering to the board should not take a week',
      description:
        'Every quarter, VPs of Engineering scramble to build slide decks explaining team performance. The data is scattered, the narrative is unclear, and the audience does not speak engineering.',
      stats: 'Engineering leaders spend 5+ hours per month preparing performance reports — CodePulse user survey',
    },
    solution: {
      title: 'One Dashboard, One Story',
      description:
        'CodePulse generates a single-page executive summary with everything a CEO or board member needs to understand engineering performance.',
      steps: [
        {
          title: 'Health Grade (A-F)',
          description: 'An aggregate score across velocity, quality, and collaboration. Simple enough for any audience.',
        },
        {
          title: 'DORA Benchmarks',
          description: 'Your DORA metrics compared against industry standards — Elite, High, Medium, or Low.',
        },
        {
          title: 'Trend Analysis',
          description: 'Quarter-over-quarter trends showing improvement trajectory or regression.',
        },
        {
          title: 'Export Ready',
          description: 'Share directly or export for slide decks. No manual data assembly required.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Health Grade', description: 'Aggregate A-F score across all dimensions', icon: 'heart' },
      { label: 'DORA Tier', description: 'Industry performance benchmark', icon: 'award' },
      { label: 'QoQ Trend', description: 'Quarter-over-quarter improvement direction', icon: 'trending-up' },
      { label: 'Team Health', description: 'Burnout signals and workload balance', icon: 'shield' },
    ],
    appRoute: '/executive',
    screenshot: '/images/screenshots/executive-summary.png',
    screenshotAlt: 'CodePulse executive summary with health grade and DORA benchmark comparison',
    relatedGuides: [
      'board-ready-engineering-metrics',
      'engineering-health-scorecard',
      'dora-metrics-guide',
      'engineering-analytics-roi-guide',
    ],
    relatedFeatures: ['dora-metrics', 'developer-comparison', 'investment-allocation'],
    testimonial: {
      quote: 'I used to spend a full day building engineering reports. Now I just share the CodePulse executive summary link.',
      author: 'Robert Striker',
      role: 'VP Engineering @ Article Fiesta',
    },
    structuredDataFeatureList: [
      'Health Grade (A-F)',
      'DORA Benchmarking',
      'Trend Analysis',
      'Export to Slides',
      'Non-Technical Audience Design',
    ],
  },
  {
    slug: 'alerts',
    title: 'Smart Engineering Alerts',
    metaTitle: 'Smart Engineering Alerts — Get Notified Before Problems Escalate',
    metaDescription:
      'Configurable alerts for cycle time spikes, stale PRs, review bottlenecks, and quality regressions. Slack and email delivery.',
    ogImage: '/images/og-features/alerts.webp',
    targetKeywords: [
      'engineering alerts tool',
      'PR alert system',
      'stale PR notifications',
      'cycle time alerts',
      'engineering metrics alerts',
      'code review alerts',
      'Slack engineering alerts',
      'developer workflow alerts',
    ],
    problemBadge: 'Proactive Detection',
    headline: 'Stop Discovering Problems in Retros',
    subtitle:
      'Configurable alerts that notify you when cycle times spike, PRs go stale, or review patterns degrade. Know before it becomes a trend.',
    painPoint: {
      title: 'By the time you notice, it has been a problem for weeks',
      description:
        'Most engineering issues — review bottlenecks, stale PRs, quality regressions — are only discovered in retrospectives. By then, the damage is done.',
      stats: 'Teams with automated alerts resolve delivery issues 3x faster than those using manual monitoring',
    },
    solution: {
      title: 'Threshold-Based Alert Engine',
      description:
        'Define custom alert rules based on your team\'s metrics. Get notified via Slack or email when thresholds are breached.',
      steps: [
        {
          title: 'Custom Thresholds',
          description: 'Set alert rules for any metric: cycle time > 72 hours, review coverage < 80%, etc.',
        },
        {
          title: 'Multi-Channel Delivery',
          description: 'Alerts delivered to Slack channels, email, or both. Route different alerts to different channels.',
        },
        {
          title: 'Smart Evaluation',
          description: 'Rules evaluated every 30 minutes against real-time data. No false alarms from temporary spikes.',
        },
        {
          title: 'Actionable Context',
          description: 'Every alert includes what triggered it, the current value, and a link to investigate.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Active Rules', description: 'Custom alert rules configured', icon: 'bell' },
      { label: 'Alert Frequency', description: 'How often alerts fire per week', icon: 'activity' },
      { label: 'Response Time', description: 'Time from alert to action', icon: 'clock' },
      { label: 'False Alarm Rate', description: 'Percentage of alerts that did not need action', icon: 'x-circle' },
    ],
    appRoute: '/alerts',
    screenshot: '/images/screenshots/alerts.png',
    screenshotAlt: 'CodePulse alert configuration showing custom threshold rules and Slack delivery',
    relatedGuides: [
      'alert-rules-guide',
      'slack-alerts-engineering-metrics',
      'detecting-risky-deployments',
      'cycle-time-breakdown-guide',
    ],
    relatedFeatures: ['cycle-time', 'risky-changes', 'deployment-analytics'],
    structuredDataFeatureList: [
      'Custom Alert Rules',
      'Slack Integration',
      'Email Notifications',
      '30-Minute Evaluation Cycle',
      'Actionable Alert Context',
    ],
  },
  {
    slug: 'benchmarks',
    title: 'Industry Benchmarks',
    metaTitle: 'Industry Benchmarks - How Your Team Compares',
    metaDescription:
      'See how your engineering team\'s delivery metrics rank against similar companies by industry and team size. Anonymized, opt-in, GitHub-native.',
    ogImage: '/images/og-features/benchmarks.webp',
    targetKeywords: [
      'engineering benchmarks',
      'software team benchmarks',
      'DORA benchmarks by industry',
      'cycle time benchmarks',
      'engineering metrics comparison',
      'developer productivity benchmarks',
      'how does my engineering team compare',
      'software delivery benchmarks',
    ],
    problemBadge: 'Industry Context',
    headline: 'Find Out If Your Engineering Org Is Actually Strong',
    subtitle:
      'Compare your team\'s delivery metrics against similar companies by industry and size. Get the answer every board asks - are we good, or do we just think we are?',
    painPoint: {
      title: 'Your metrics mean nothing without context',
      description:
        'A 40-hour cycle time looks alarming until you learn it beats most teams your size. Leaders report numbers up the chain with no way to say whether they signal a problem or a win. Internal trends show movement, but never tell you where you stand against the wider market.',
      stats:
        'In the 2025 CodePulse Benchmark Study (803K PRs), most teams spent the majority of cycle time waiting for review rather than coding - a pattern invisible without an outside reference point.',
    },
    solution: {
      title: 'Anonymized comparison against teams like yours',
      description:
        'Opt in to contribute your anonymized metrics, and CodePulse places your team against a percentile distribution drawn from organizations in your industry and size band. The question shifts from whether you are improving to whether you are strong against the market.',
      steps: [
        {
          title: 'Opt in, stay anonymous',
          description:
            'Benchmarking is off by default. When you turn it on, only aggregated and anonymized metrics join the dataset. Nothing identifies your team, and you can opt out at any time.',
        },
        {
          title: 'Match your peer group',
          description:
            'Pick your industry from seven categories - SaaS, Fintech, Healthcare, E-commerce, Enterprise, Consumer, or Other - and a team-size band from 1-10 up to 200+ developers, so comparisons reflect companies like yours.',
        },
        {
          title: 'See your percentile, not a ranking of people',
          description:
            'Each metric shows where your team lands against the p10 to p90 distribution, with a clear tier from top 10% down to bottom 10%. This measures the team and the system, never individual contributors.',
        },
        {
          title: 'Trust the sample',
          description:
            'Comparisons only appear once at least 10 organizations have contributed to your segment, so a percentile reflects a real distribution rather than a handful of outliers.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Percentile Rank', description: 'Where your team lands from p10 to p90 against similar organizations, with a top 10% to bottom 10% performance tier', icon: 'pie-chart' },
      { label: 'Cycle Time vs Peers', description: 'Median time from first commit to merge, compared against teams in your industry and size band', icon: 'clock' },
      { label: 'Review Coverage and Quality', description: 'Share of PRs reviewed and merge-without-approval rate, benchmarked so you can judge process health, not police people', icon: 'users' },
      { label: 'Delivery and Throughput', description: 'Deployment frequency, PRs merged, and PR size measured against the market median for your segment', icon: 'trending-up' },
    ],
    appRoute: '/benchmarks',
    screenshot: '/images/screenshots/dashboard-team-overview-full.webp',
    screenshotAlt: 'CodePulse industry benchmarks showing percentile ranking against similar teams',
    relatedGuides: ['pr-cycle-time-benchmarks', 'engineering-health-scorecard', 'dora-metrics-guide'],
    relatedFeatures: ['dora-metrics', 'cycle-time', 'executive-summary'],
    structuredDataFeatureList: [
      'Anonymized opt-in benchmarking',
      'Industry and team-size peer matching',
      'Percentile ranking against similar teams',
      'Cycle time, review coverage, and deployment frequency benchmarks',
      'Minimum-sample-size protection for honest comparisons',
    ],
  },
  {
    slug: 'developer-surveys',
    title: 'Developer Experience Surveys',
    metaTitle: 'Developer Experience Surveys - CodePulse',
    metaDescription:
      'Run developer experience surveys inside your GitHub analytics. Blend honest team feedback with real delivery data to catch burnout early.',
    ogImage: '/images/og-features/developer-surveys.webp',
    targetKeywords: [
      'developer experience surveys',
      'developer satisfaction survey',
      'engineering team morale survey',
      'developer burnout signals',
      'SPACE framework satisfaction',
      'team sentiment tracking',
      'developer wellbeing survey',
      'engineering survey tool',
    ],
    problemBadge: 'Beyond The Metrics',
    headline: 'Hear What The Dashboard Cannot Show You',
    subtitle:
      'Pull request data tells you what your team shipped. It cannot tell you whether your engineers are frustrated with their tooling, stretched thin, or about to leave. Developer experience surveys give the team a voice, and put that voice next to the GitHub data you already trust.',
    painPoint: {
      title: 'Delivery looks fine until someone resigns',
      description:
        'Throughput holds steady right up to the day a senior engineer hands in notice. By then the cost is already paid - a backfill that takes months, knowledge that walks out the door, and a team that quietly absorbs the gap. Raw metrics are a lagging signal for how people feel. Burnout is a management failure, not a personal one, and you cannot manage what you only find out about in an exit interview.',
      stats:
        'The SPACE framework (Forsgren et al.) names satisfaction and well-being as a dimension you cannot infer from activity data alone.',
    },
    solution: {
      title: 'Surveys and system data in one place',
      description:
        'CodePulse runs developer experience surveys where your engineering data already lives. You see how the team feels next to cycle time, review load, and PR throughput - qualitative signal and quantitative signal read together. Surveys are developmental and team-first. They are never used to rank individuals.',
      steps: [
        {
          title: 'Build your question set',
          description:
            'Create and order your own questions, or start from a default set covering satisfaction, well-being, tools, team, and process. Choose the format that fits each question - 5-point Likert, 7-point Likert, NPS, or free text.',
        },
        {
          title: 'Set the cadence',
          description:
            'Run surveys weekly, monthly, or quarterly. Surveys appear to signed-in developers and respect your frequency and participation rules, so people are asked at a steady rhythm rather than buried in reminders.',
        },
        {
          title: 'Collect honest feedback',
          description:
            'Responses are anonymous by default, so people answer truthfully. The admin view never exposes who left an anonymous response, regardless of role, so the anonymity promise holds.',
        },
        {
          title: 'Read feeling against reality',
          description:
            'Track category scores over time, review responses, and feed satisfaction into the SPACE dimension alongside your GitHub delivery metrics. When sentiment dips while review load climbs, you see both signals together.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Category Scores', description: 'Average scores across satisfaction, well-being, tools, team, and process, so you know which part of the experience needs attention', icon: 'activity' },
      { label: 'Participation Rate', description: 'The share of the team responding each period - a direct read on whether people feel heard', icon: 'users' },
      { label: 'Sentiment Over Time', description: 'Weekly trend lines per category reveal whether morale is recovering or sliding before it shows up in delivery', icon: 'trending-up' },
      { label: 'SPACE Satisfaction', description: 'Survey scores feed the satisfaction dimension of the SPACE framework, sitting next to performance and activity data', icon: 'heart' },
    ],
    appRoute: '/surveys',
    screenshot: '/images/screenshots/space-metrics.png',
    screenshotAlt: 'CodePulse developer experience surveys feeding the SPACE satisfaction dimension',
    relatedGuides: ['burnout-signals-git-data', 'measure-team-performance-without-micromanaging', 'engineering-metrics-trust-guide'],
    relatedFeatures: ['space-framework', 'review-insights', 'developer-comparison'],
    structuredDataFeatureList: [
      'Custom survey questions with Likert-5, Likert-7, NPS, and free-text formats',
      'Question categories for satisfaction, well-being, tools, team, and process',
      'Weekly, monthly, or quarterly cadence with frequency and participation rules',
      'Anonymous-by-default responses that the admin view never de-anonymizes',
      'SPACE framework satisfaction dimension blended with GitHub delivery metrics',
    ],
  },
  {
    slug: 'integrations',
    title: 'Jira, Linear & Azure DevOps Integrations',
    metaTitle: 'GitHub Analytics With Jira & Linear - CodePulse',
    metaDescription:
      'Connect Jira, Linear, and Azure DevOps to CodePulse. Issue context enriches your GitHub data with work classification and lead time. Setup in minutes.',
    ogImage: '/images/og-features/integrations.webp',
    targetKeywords: [
      'github analytics jira integration',
      'link prs to jira issues',
      'linear github integration',
      'issue tracker engineering metrics',
      'work classification engineering',
      'software investment allocation',
      'jira pull request analytics',
      'engineering analytics integrations',
    ],
    problemBadge: 'Stack Fit',
    headline: 'GitHub-native depth, with your issue tracker connected',
    subtitle:
      'CodePulse reads your code activity directly from GitHub, then links it to the issues in Jira, Linear, and Azure DevOps. You get code-level detail and the planning context around it, without standing up a data warehouse or moving off the tools your team already uses.',
    painPoint: {
      title: 'A GitHub-only tool will not fit our stack',
      description:
        'Your team plans in Jira or Linear and ships in GitHub or Azure DevOps. Most code-analytics tools see only one half. Either they read pull requests with no idea what work they belong to, or they sit on top of the issue tracker and never look at the code. So you end up exporting CSVs and stitching the two together by hand. The question every VP of Engineering asks before buying is the same - will this actually work with what we run today?',
      stats:
        'Engineering teams commonly run separate tools for planning and code, leaving the link between an issue and the work that delivered it manual and unreliable.',
    },
    solution: {
      title: 'Code and issue context in one place',
      description:
        'CodePulse stays GitHub-native at its core and treats Jira, Linear, and Azure DevOps as first-class connections. Connect over OAuth in minutes, and issue keys in your branch names and PR titles are matched automatically. The result is one view where a pull request carries the issue it delivered, its work type, and its lead time - no warehouse, no per-seat math, flat pricing.',
      steps: [
        {
          title: 'Connect over OAuth',
          description:
            'Authorize Jira (Atlassian Cloud) or Linear with OAuth - no API tokens to copy or service accounts to provision. Azure DevOps connects as a code and pull-request source alongside GitHub. Setup takes minutes, not a procurement cycle.',
        },
        {
          title: 'Issues sync automatically',
          description:
            'CodePulse pulls issues on a schedule, with an initial lookback of about 400 days so historical work is covered from day one. Jira sync uses JQL with per-project watermarks, so each project stays current without re-reading everything.',
        },
        {
          title: 'PRs link to issues',
          description:
            'Issue keys like ENG-123 are detected in branch names and PR titles, then matched to the synced issue. The link is automatic - your engineers keep working the way they already do.',
        },
        {
          title: 'Context enriches your metrics',
          description:
            'Linked issues feed work classification (feature, bug fix, tech debt, and more) and issue lead time. Classification follows a clear priority - GitHub labels first, then issue-tracker labels, then AI, then issue type - and you can override the mappings per project.',
        },
      ],
    },
    keyMetrics: [
      { label: 'OAuth Setup in Minutes', description: 'Authorize Jira or Linear with OAuth and start syncing the same day. No tokens, no warehouse, no separate analyst to maintain the join', icon: 'zap' },
      { label: 'Automatic PR-to-Issue Links', description: 'Issue keys in branch names and PR titles are matched to synced issues, so every pull request carries the work it delivered', icon: 'git-merge' },
      { label: 'Work Classification', description: 'Linked issue labels and types drive investment categories - feature, bug fix, tech debt, maintenance, infrastructure - with per-project overrides', icon: 'pie-chart' },
      { label: 'Issue Lead Time and Status History', description: 'Status changelogs are synced from Jira and Linear, so you can see how long work sat in each state, not just when it closed', icon: 'clock' },
    ],
    appRoute: '/integrations',
    screenshot: '/images/screenshots/dashboard-team-overview-full.webp',
    screenshotAlt: 'CodePulse connecting Jira and Linear issues to GitHub pull requests',
    relatedGuides: ['progress-tracking-without-jira', 'feature-vs-maintenance-balance', 'engineering-analytics-tools-comparison'],
    relatedFeatures: ['investment-allocation', 'issue-lead-time', 'sprint-retro'],
    structuredDataFeatureList: [
      'Connect Jira (Atlassian Cloud) and Linear over OAuth in minutes',
      'Automatic linking of pull requests to issues via keys in branch names and PR titles',
      'Work classification from issue labels and types, with per-project overrides',
      'Issue status changelog and lead-time tracking from Jira and Linear',
      'Azure DevOps supported as a code and pull-request source alongside GitHub',
    ],
  },
  {
    slug: 'velocity',
    title: 'Velocity Trends',
    metaTitle: 'Velocity Trends - CodePulse',
    metaDescription:
      'See whether your engineering team is trending up or down with a composite Velocity Score across speed, throughput, quality, and collaboration.',
    ogImage: '/images/og-features/velocity.webp',
    targetKeywords: [
      'engineering velocity',
      'team velocity tracking',
      'velocity trends',
      'software delivery velocity',
      'engineering team performance',
      'velocity score',
      'cycle time trend',
      'github engineering analytics',
    ],
    problemBadge: 'Direction, Not Snapshots',
    headline: 'Know if your engineering team is trending up or down, and why',
    subtitle:
      'Velocity Trends turns months of GitHub activity into one read on team direction. A composite Velocity Score combines speed, throughput, quality, and collaboration, so a faster team that ships broken or unreviewed code never looks healthy. Velocity is a signal, not a target.',
    painPoint: {
      title: 'You can feel the team slowing down, but you cannot prove it',
      description:
        'Most leaders track velocity through gut feel and end-of-quarter retros. A single throughput number tells you how many PRs merged, but not whether quality held, whether reviews kept up, or whether last month was better than this one. So slowdowns surface late, after a deadline slips, and the conversation turns into opinion versus opinion instead of a shared view of the trend.',
      stats:
        'In the 2025 CodePulse Benchmark Study (803K PRs), throughput alone proved a poor read on team health - speed only counts when quality and review coverage hold with it.',
    },
    solution: {
      title: 'One score, four dimensions, and a trend you can act on',
      description:
        'CodePulse reads your GitHub history and builds a Velocity Score from four dimensions that pull against each other on purpose. Speed comes from cycle time, throughput from PRs merged and deploy frequency, quality from test pass rate and review coverage, and collaboration from review coverage and approval rates. A team only scores well when all four hold together.',
      steps: [
        {
          title: 'Connect GitHub in about five minutes',
          description:
            'CodePulse is GitHub-native. Install the app, pick your repositories, and the trend builds from your existing history with no agents and no instrumentation in your codebase.',
        },
        {
          title: 'Read the composite Velocity Score',
          description:
            'The score blends speed, throughput, quality, and collaboration into one number with a letter grade. A team that ships fast but skips review or breaks the build will not score well, because every dimension counts.',
        },
        {
          title: 'Find the bottleneck',
          description:
            'CodePulse flags the lowest-scoring dimension and names the specific phase holding the team back, such as review wait time or test failures, so the next improvement is obvious rather than guessed.',
        },
        {
          title: 'Watch the trend, not the moment',
          description:
            'The cycle time trend chart aggregates by day, week, or month across your chosen range. The same time, repository, and bot filters apply everywhere so the numbers stay comparable.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Composite Velocity Score', description: 'One score and letter grade built from speed, throughput, quality, and collaboration, so balanced teams score well and one-dimensional teams do not', icon: 'activity' },
      { label: 'Cycle Time Trend', description: 'Average time from first commit to merge, charted by day, week, or month so you can see whether delivery speed is improving or slipping', icon: 'trending-up' },
      { label: 'Throughput', description: 'PRs merged across the team for the selected period, weighted into the score rather than treated as a standalone target', icon: 'rocket' },
      { label: 'Bottleneck Callout', description: 'The lowest-scoring dimension is surfaced with the specific phase to address next, turning the score into a clear improvement', icon: 'zap' },
    ],
    appRoute: '/velocity',
    screenshot: '/images/screenshots/dashboard-velocity-graph.webp',
    screenshotAlt: 'CodePulse velocity trend showing composite score across speed, throughput, quality, and collaboration',
    relatedGuides: ['engineering-velocity-metrics-guide', 'reduce-pr-cycle-time', 'developer-productivity-metrics-guide'],
    relatedFeatures: ['cycle-time', 'dora-metrics', 'forecasting'],
    structuredDataFeatureList: [
      'Composite Velocity Score across speed, throughput, quality, and collaboration',
      'Cycle time trend charts with daily, weekly, and monthly granularity',
      'Throughput tracking based on PRs merged with trend direction',
      'Automatic bottleneck detection that names the dimension and phase to fix',
      'Shared time period, repository, and bot filters for consistent views',
    ],
  },
  {
    slug: 'issue-lead-time',
    title: 'Issue Lead Time',
    metaTitle: 'Issue Lead Time - Find Where Work Stalls',
    metaDescription:
      'See end-to-end issue lead time from creation to merge. Break down time in every Jira or Linear workflow status and find where delivery stalls.',
    ogImage: '/images/og-features/issue-lead-time.webp',
    targetKeywords: [
      'issue lead time',
      'lead time metric',
      'time in status',
      'workflow bottleneck analysis',
      'jira lead time',
      'linear cycle time',
      'delivery lead time tracking',
      'software delivery bottlenecks',
    ],
    problemBadge: 'Hidden Delays',
    headline: 'See where work waits before it ever reaches code',
    subtitle:
      'PR cycle time shows where code waits. Issue Lead Time shows where work waits before and around the code - from the moment an issue is created to the moment its PR merges. Connect Jira or Linear and watch the full delivery path, status by status.',
    painPoint: {
      title: 'Your delivery delays live outside the pull request',
      description:
        'Engineering leaders measure code review and merge times closely, then wonder why features still take weeks to ship. The real delay often sits earlier: issues that age in the backlog, tickets parked in review, work stuck and blocked with no owner. Without visibility into the issue lifecycle, you are tuning the last mile while the first mile quietly eats your roadmap.',
      stats:
        'The slowest part of delivery is usually the part nobody is watching - time work spends waiting in the backlog and across workflow statuses, not in code review.',
    },
    solution: {
      title: 'One view of the full issue-to-merge path',
      description:
        'Issue Lead Time reads the status changelog from Jira or Linear and reconstructs how long each issue spends in every workflow state. It pairs that with the linked pull request to give you a single, end-to-end picture - backlog dwell, planning, coding, waiting, review, and merge.',
      steps: [
        {
          title: 'Connect Jira or Linear',
          description:
            'A standard OAuth connection pulls issue history and status changelogs into CodePulse. No agents, no scripts. Setup takes minutes, and the data populates as your trackers sync.',
        },
        {
          title: 'Measure end-to-end lead time',
          description:
            'See median, average, and P90 lead time from issue created to PR merged, plus backlog dwell time and time to first commit. These are lead times for the whole delivery path, not just the code review window.',
        },
        {
          title: 'Break down time in every status',
          description:
            'Status aging shows how long work sits in each workflow state - backlog, in progress, blocked, and the rest - using real transition timestamps. The longest-running statuses surface first.',
        },
        {
          title: 'Trace bottlenecks to repos and teams',
          description:
            'Lead time breaks down by repository and by team, and backlog dwell drivers highlight where planning time piles up. You fix the workflow, not the people - this is a system view, not a scorecard.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Issue-to-Merge Lead Time', description: 'Median, average, and P90 time from issue creation to PR merge - the true end-to-end delivery clock', icon: 'clock' },
      { label: 'Backlog Dwell Time', description: 'How long issues sit after creation before any work or pull request begins', icon: 'pause' },
      { label: 'Time in Status', description: 'Average, median, and P90 time spent in each Jira or Linear workflow state, built from status change history', icon: 'pie-chart' },
      { label: 'Bottleneck by Repo and Team', description: 'Where issue-to-merge time is longest across repositories and teams, so you target the right part of the system', icon: 'activity' },
    ],
    appRoute: '/issue-lead-time',
    screenshot: '/images/screenshots/dashboard-velocity-graph.webp',
    screenshotAlt: 'CodePulse issue lead time breakdown showing time spent in each workflow status',
    relatedGuides: ['deployment-frequency-lead-time-guide', 'capacity-planning-pr-data', 'progress-tracking-without-jira'],
    relatedFeatures: ['cycle-time', 'integrations', 'forecasting'],
    structuredDataFeatureList: [
      'End-to-end issue lead time from creation to PR merge (median, average, P90)',
      'Time-in-status breakdown from Jira and Linear status changelogs',
      'Backlog dwell time and time-to-first-commit tracking',
      'Lead time bottleneck analysis by repository and by team',
      'Requires a connected Jira or Linear issue tracker',
    ],
  },
  {
    slug: 'awards',
    title: 'Engineering Awards',
    metaTitle: 'Engineering Awards - Recognise Great Teammates',
    metaDescription:
      'Celebrate the engineers who review, mentor, test, and unblock teammates. Recognition built on healthy behaviour, not commit counts or lines of code.',
    ogImage: '/images/og-features/awards.webp',
    targetKeywords: [
      'engineering awards',
      'developer recognition software',
      'reward code reviewers',
      'team recognition engineering',
      'peer recognition developers',
      'code review recognition',
      'celebrate engineering culture',
      'github team awards',
    ],
    problemBadge: 'Invisible Work',
    headline: 'Recognise the work that holds your team together',
    subtitle:
      'The best teammates rarely top a commit chart. They review carefully, mentor others, write tests, and unblock people who are stuck. Engineering Awards surfaces those contributions so you can celebrate them out loud, not just the work that happens to be loud.',
    painPoint: {
      title: 'Good teammates go unseen, and so does your culture',
      description:
        'Reviewing a teammate PR, leaving a thoughtful comment, mentoring a junior, writing the test that catches a bug before customers do. This is the work that keeps teams healthy, and it almost never shows up in a dashboard. So recognition drifts toward whoever shipped the most visible feature, and the engineers quietly doing the connective work feel invisible. When leaders reward raw output, they train people to optimise for the wrong thing.',
      stats:
        'Lines of code and commit counts measure activity, not value - and rewarding them trains teams to ship bigger PRs with fewer reviews.',
    },
    solution: {
      title: 'Awards that celebrate being a good teammate',
      description:
        'Engineering Awards reads your existing GitHub activity and recognises the behaviours that make teams stronger - fast and helpful reviews, careful and reviewable code, mentorship, broad collaboration, and test contribution. You cannot game being a good teammate, which is exactly the point.',
      steps: [
        {
          title: 'Connect your work GitHub account',
          description:
            'A five-minute, GitHub-native setup. Awards are calculated from the PRs, reviews, and comments your team already creates. There is nothing extra to install or maintain.',
        },
        {
          title: 'Surface healthy behaviour automatically',
          description:
            'CodePulse highlights the engineers who review quickly and helpfully, unblock stuck teammates, keep PRs small and reviewable, mentor others, and contribute tests. Each category shows the top contributors with the metric behind it.',
        },
        {
          title: 'Recognise review quality, not just review count',
          description:
            'Dedicated Review Quality awards celebrate the engineers catching bugs and security issues, leaving thorough comments, and giving constructive feedback. These reward the substance of a review, not rubber stamps.',
        },
        {
          title: 'Bring it into your team rituals',
          description:
            'Use awards as concrete material for shout-outs, retros, and one-on-ones. Export any category to CSV. Frame recognition around behaviours the whole team can learn from and repeat.',
        },
      ],
    },
    keyMetrics: [
      { label: 'Helpful Reviewer Awards', description: 'Lightning Reviewer, Unblock Hero, and Code Mentor recognise the engineers who respond fast, clear blockers, and give the most reviews to teammates', icon: 'heart' },
      { label: 'Code Quality Awards', description: 'Quality Guardian, Clean Code Advocate, and Careful Coder celebrate high test pass rates and small, reviewable changes rather than sheer volume', icon: 'shield' },
      { label: 'Collaboration Awards', description: 'Team Player, Collaboration Star, and Cross-Pollinator highlight the people who review more than they create and work across the codebase', icon: 'users' },
      { label: 'Review Quality and Test Champions', description: 'Bug Hunter, Security Sentinel, Knowledge Sharer, plus Test Champions and Polyglot leaderboards recognise depth, rigour, and breadth', icon: 'award' },
    ],
    appRoute: '/awards',
    screenshot: '/images/screenshots/awards-leaderboard-full.webp',
    screenshotAlt: 'CodePulse engineering awards celebrating healthy review and collaboration behaviour',
    relatedGuides: ['developer-recognition-awards', 'code-review-culture-sentiment', 'measure-team-performance-without-micromanaging'],
    relatedFeatures: ['review-network', 'developer-comparison', 'review-insights'],
    structuredDataFeatureList: [
      'Positive-recognition awards across speed, quality, collaboration, and consistency themes',
      'Review Quality awards for catching bugs and security issues and thorough feedback',
      'Test Champions leaderboard recognising engineers who contribute the most test code',
      'Polyglot Developers leaderboard celebrating breadth across programming languages',
      'Time period, repository, and exclude-bots filters with one-click CSV export',
    ],
  },
];

export function getFeaturePageBySlug(slug: string): FeaturePageData | undefined {
  return featurePages.find((f) => f.slug === slug);
}

export function getAllFeaturePageSlugs(): string[] {
  return featurePages.map((f) => f.slug);
}
